<script setup lang="ts">
import LinkComponent from "~/templates/components/cms/LinkComponent.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import { ProductTeaser } from "~/templates/partials/navigation/ProductTeaser";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage, filterNodesByType, mapNodes } from "~/utils/helper/magnolia";

export interface LevelTwoListProps {
  level: {
    image?: object;
    teaserLink: {
      label: string;
      link: string;
      type: string;
    };
    teaserHeadline: string;
    teaserSubline: string;
  };
}

const { level } = defineProps<LevelTwoListProps>();

const BREAKPOINTS = {
  start: DIMENSIONS["start"].ratio["3/2"],
  md: DIMENSIONS["md"].width["1/2"].ratio["3/2"],
};

const columns = computed(() => filterNodesByType(mapNodes(level), "nav:linkgroup"));
</script>

<template>
  <div
    v-if="columns?.length > 0"
    class="relative grid gap-4 md:grid-cols-2 xl:grid-cols-4 xl:gap-8"
    role="list"
  >
    <template v-for="(column, index) in columns" :key="`column-${index}`">
      <div v-if="index < 4" class="group/list" role="listitem">
        <div class="relative xl:text-lg">
          <div
            v-if="containsImage(column.image)"
            class="mb-4 hidden aspect-3/2 overflow-hidden rounded-2xl xl:block"
          >
            <PictureElement
              :img="column.image"
              :breakpoints="BREAKPOINTS"
              img-class="motion-safe:transition-transform object-cover rounded-2xl aspect-3/2 w-full group-hover/list:scale-110"
            />
          </div>
          <LinkComponent
            :link="column?.groupLink"
            :stretch-link="true"
            optional-link-classes="font-medium hover:text-deep-blue"
          >
            {{ column.title }}
          </LinkComponent>
        </div>
        <ul
          v-if="filterNodesByType(mapNodes(column), 'nav:link').length > 0"
          class="mt-2 flex flex-col gap-2"
        >
          <li
            v-for="link in filterNodesByType(mapNodes(column), 'nav:link')"
            :key="link?.link?.label"
          >
            <LinkComponent :link="link?.link" optional-link-classes="hover:text-deep-blue">
              {{ link?.link?.label }}
            </LinkComponent>
          </li>
        </ul>
      </div>
    </template>
    <ProductTeaser
      v-if="columns.length < 4 && level.teaserHeadline"
      role="listitem"
      class="col-span-full xl:col-start-4 xl:col-end-4"
      :heading="level.teaserHeadline"
      :subline="level.teaserSubline"
      :link="level.teaserLink"
      :image="level.image"
    />
  </div>
</template>
